import { useT } from "@transifex/react";
import { useState } from "react";
import { ArrowsInSimpleIcon, ArrowsOutSimpleIcon, KeyIcon } from "@phosphor-icons/react";
import PhaseSixIcon from "../../../basic/phaseSixIcon/PhaseSixIcon";
import { PhosphorIcon } from "../../../basic/phosphorIcon/PhosphorIcon";
import BasePopupWrapper, { TitleColorVariants } from "../../../basic/popups/BasePopupWrapper";
import type { SchoolClassVisibilities } from "../../../enums/schoolClasses";
import { getClassroomDisplayUrl, getClassroomUrl } from "../../../helpers/schoolClassHelpers";
import useBreakpoints from "../../../hooks/useBreakpoints";
import useQrCodeDataUrl from "../../../hooks/useQrCodeDataUrl";
import { isPrivateSchoolClass } from "../../../utils/schoolClassUtils";
import ClassLinkCopyButton from "./ClassLinkCopyButton";

interface ShareClassAccessPopupProps {
    isOpen: boolean;
    onClose: () => void;
    schoolClassId: string;
    classVisibility: SchoolClassVisibilities;
    classMembersCount: number;
    areClassMembersLoaded: boolean;
    onDownloadCodes: () => void;
    onCopyLink: () => void;
    onCopyFailed: () => void;
}

// Rendered once at the enlarged size; the compact view scales the same image down.
const QR_CODE_RENDER_WIDTH = 400;

/**
 * Class link + QR code, the route by which pupils join a class themselves. Private classes add the
 * printable access codes once students exist.
 */
const ShareClassAccessPopup = (props: ShareClassAccessPopupProps) => {
    const t = useT();
    const { isAboveSM } = useBreakpoints();

    const [isQrCodeEnlarged, setIsQrCodeEnlarged] = useState(false);

    const isPrivate = isPrivateSchoolClass(props.classVisibility);
    // Hidden only for a confirmed-empty class: a pending or failed load must keep the sole route to
    // the printable codes open.
    const canDownloadCodes = isPrivate && (!props.areClassMembersLoaded || props.classMembersCount > 0);
    const classUrl = getClassroomUrl(props.schoolClassId);
    const classDisplayUrl = getClassroomDisplayUrl(props.schoolClassId);
    const qrDataUrl = useQrCodeDataUrl(props.isOpen ? classUrl : "", QR_CODE_RENDER_WIDTH);

    const t_title = t("Access to the classroom", { _tags: "SchoolClasses, ShareClassAccessPopup" });
    const t_scanOrOpen = t("Scan the QR code or open the link", { _tags: "SchoolClasses, ShareClassAccessPopup" });
    const t_logInOrJoin = t("Log in with your personal code or join as a new student", {
        _tags: "SchoolClasses, ShareClassAccessPopup",
    });
    const t_startWithoutLogin = t("Get started right away, no login needed", {
        _tags: "SchoolClasses, ShareClassAccessPopup",
    });
    const t_enlarge = t("Enlarge", { _tags: "SchoolClasses, ShareClassAccessPopup" });
    const t_shrink = t("Shrink", { _tags: "SchoolClasses, ShareClassAccessPopup" });
    const t_downloadCountedAccessDetails = t("Download {count} access details", {
        _tags: "SchoolClasses, ShareClassAccessPopup",
        count: props.classMembersCount,
    });
    const t_downloadAccessDetails = t("Download access details", { _tags: "SchoolClasses, ShareClassAccessPopup" });
    const t_classLinkAndCodes = t("Class link & personal codes", { _tags: "SchoolClasses, ShareClassAccessPopup" });
    const t_forPrinting = t("For printing and handing out", { _tags: "SchoolClasses, ShareClassAccessPopup" });
    const t_close = t("Close", { _tags: "SchoolClasses, ShareClassAccessPopup" });
    const t_qrCode = t("QR Code", { _tags: "SchoolClasses, ShareClassAccessPopup" });
    const t_sr_copyLink = t("Copy link", { _tags: "SchoolClasses, ShareClassAccessPopup, ScreenReaderOnly" });

    // The count is only known once the member load succeeded.
    const downloadLabel = props.areClassMembersLoaded ? t_downloadCountedAccessDetails : t_downloadAccessDetails;

    const handleClose = () => {
        setIsQrCodeEnlarged(false);
        props.onClose();
    };

    // The QR code and its label form one toggle: teachers click the code itself to enlarge it.
    const renderQrCodeToggle = () => (
        <button
            type="button"
            data-testid="button-toggle-qr-code-size"
            aria-expanded={isQrCodeEnlarged}
            onClick={() => setIsQrCodeEnlarged((prev) => !prev)}
            className={`group flex flex-col items-center gap-xs bg-transparent border-none p-no cursor-pointer ${
                isQrCodeEnlarged ? "w-full" : "shrink-0"
            }`}
        >
            {qrDataUrl && (
                <img
                    data-testid="share-class-access-qr"
                    src={qrDataUrl}
                    alt={t_qrCode}
                    className={isQrCodeEnlarged ? "w-full h-auto" : "w-[104px] h-auto"}
                />
            )}
            <span className="flex items-center justify-center gap-xs text-p6-grey-mid group-hover:text-p6-grey-dark">
                <span className="text-s">{isQrCodeEnlarged ? t_shrink : t_enlarge}</span>
                <PhosphorIcon
                    phosphorIcon={isQrCodeEnlarged ? ArrowsInSimpleIcon : ArrowsOutSimpleIcon}
                    fontSize="text-xl"
                />
            </span>
        </button>
    );

    const renderStep = (number: number, text: string) => (
        <div className="flex items-center gap-s w-full">
            <span className="flex items-center justify-center w-xl h-xl shrink-0 rounded-circle bg-p6-orange-light-pastel text-l font-semibold text-p6-orange-darker">
                {number}
            </span>
            <span className="flex-1 min-w-0 text-m text-p6-grey-dark">{text}</span>
        </div>
    );

    const renderQrCodeAndSteps = () => (
        <div
            className={`flex items-center w-full ${isAboveSM ? "flex-row gap-m p-s" : "flex-col gap-s px-s pt-m pb-s"}`}
        >
            {renderQrCodeToggle()}
            <div
                className={`flex flex-col gap-xs ${isAboveSM ? "flex-1 min-w-0" : "w-full"}`}
                data-testid="share-class-access-steps"
            >
                {renderStep(1, t_scanOrOpen)}
                {renderStep(2, isPrivate ? t_logInOrJoin : t_startWithoutLogin)}
            </div>
        </div>
    );

    const renderAccessBox = () => (
        <div className="flex flex-col w-full border-2 border-p6-orange-brighter rounded-medium">
            {renderQrCodeAndSteps()}
            <div className="px-s w-full">
                <div className="h-px w-full bg-p6-orange-pastel" />
            </div>
            <ClassLinkCopyButton
                data-testid="share-class-access-link"
                ariaLabel={t_sr_copyLink}
                displayUrl={classDisplayUrl}
                copyUrl={classUrl}
                onCopied={props.onCopyLink}
                onCopyFailed={props.onCopyFailed}
                className="w-full p-s"
                urlClassName="flex-1"
                urlTestid="share-class-access-link-url"
            />
        </div>
    );

    const renderDownloadIcon = () => (
        <PhaseSixIcon
            name="download"
            className="text-xl text-p6-grey-dark shrink-0"
        />
    );

    const renderDownloadExplanation = () => (
        <div className="flex flex-col text-s text-p6-grey-mid">
            <span>{t_classLinkAndCodes}</span>
            <span>{t_forPrinting}</span>
        </div>
    );

    // Desktop: key icon beside the text, trailing download icon. Mobile: smaller key icon and the
    // download icon share the heading row, the explanation spans the full width below.
    const renderDownloadBox = () => (
        <button
            type="button"
            data-testid="button-download-codes"
            onClick={props.onDownloadCodes}
            className="flex items-center gap-s w-full p-s border-2 border-p6-grey-light rounded-small-container bg-white hover:bg-background-light cursor-pointer text-left"
        >
            {isAboveSM ? (
                <>
                    <PhosphorIcon
                        phosphorIcon={KeyIcon}
                        fontSize="text-xxl"
                        color="text-p6-grey-dark"
                    />
                    <div className="flex flex-col gap-xxs flex-1 min-w-0">
                        <span className="text-m text-p6-grey-dark">{downloadLabel}</span>
                        {renderDownloadExplanation()}
                    </div>
                    {renderDownloadIcon()}
                </>
            ) : (
                <div className="flex flex-col gap-xs flex-1 min-w-0">
                    <div className="flex items-center gap-s w-full">
                        <PhosphorIcon
                            phosphorIcon={KeyIcon}
                            fontSize="text-xl"
                            color="text-p6-grey-dark"
                        />
                        <span className="flex-1 min-w-0 text-m text-p6-grey-dark">{downloadLabel}</span>
                        {renderDownloadIcon()}
                    </div>
                    {renderDownloadExplanation()}
                </div>
            )}
        </button>
    );

    return (
        <BasePopupWrapper
            isOpen={props.isOpen}
            onClose={handleClose}
            title={t_title}
            titleColor={TitleColorVariants.DEFAULT}
            maxWidth={448}
            secondaryButton={{
                text: t_close,
                onClick: handleClose,
                className: "min-w-[180px]",
            }}
        >
            <div
                className="flex flex-col gap-s w-full"
                data-testid="share-class-access-popup"
            >
                {/* Enlarged, the QR code takes over the popup so it scans from the back of the room. */}
                {isQrCodeEnlarged ? (
                    renderQrCodeToggle()
                ) : (
                    <>
                        {renderAccessBox()}
                        {canDownloadCodes && renderDownloadBox()}
                    </>
                )}
            </div>
        </BasePopupWrapper>
    );
};

export default ShareClassAccessPopup;
